export const formatPrice = (amount, currency = "BRL", language = "pt-BR") => {
    return new Intl.NumberFormat(language, {
        style: "currency",
        currency: currency,
    }).format(amount / 100);
};

export const formatPercentage = (amount, language = "pt-BR") => {
    return new Intl.NumberFormat(language, {
        style: "percent",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount / 100);
};

export const copyToClipboard = (text, toast = null) => {
    return new Promise((resolve, reject) => {
        navigator.clipboard
            .writeText(text)
            .then(() => {
                if (toast) {
                    toast("Copiado para a área de transferência!", "success");
                }

                resolve();
            })
            .catch((e) => reject(e));
    });
};

export const getPrice = (offer, payment_method) => {
    if (payment_method === "pix") {
        if (offer?.price_for_pix && offer?.price_for_pix > 0) {
            return offer?.price_for_pix;
        }
    }

    if (payment_method === "credit_card") {
        if (offer?.price_for_credit_card && offer?.price_for_credit_card > 0) {
            return offer?.price_for_credit_card;
        }
    }

    if (payment_method === "billet") {
        if (offer?.price_for_billet && offer?.price_for_billet > 0) {
            return offer?.price_for_billet;
        }
    }

    return offer?.price ?? 0;
};

export const getOriginalPrice = (offer, payment_method) => {
    if (payment_method === "pix") {
        if (
            offer?.original_price_for_pix &&
            offer?.original_price_for_pix > 0
        ) {
            return offer?.original_price_for_pix;
        }
    }

    if (payment_method === "credit_card") {
        if (
            offer?.original_price_for_credit_card &&
            offer?.original_price_for_credit_card > 0
        ) {
            return offer?.original_price_for_credit_card;
        }
    }

    if (payment_method === "billet") {
        if (
            offer?.original_price_for_billet &&
            offer?.original_price_for_billet > 0
        ) {
            return offer?.original_price_for_billet;
        }
    }

    if (offer?.original_price && offer?.original_price > 0) {
        return offer?.original_price;
    }

    return 0;
};

export const formatDate = (date, language = "pt-BR", options = {}) => {
    return new Date(date).toLocaleDateString(language, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        ...options,
    });
};

export const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export const asset = (path) => {
    const base =
        (typeof window !== "undefined" && window.assetBaseUrl) ||
        import.meta.env.VITE_ASSET_URL ||
        "/";
    return base.replace(/\/$/, "") + "/" + path.replace(/^\//, "");
};

export const lightenColor = (hex, percent) => {
    let num = parseInt(hex.replace("#", ""), 16);

    let r = (num >> 16);
    let g = (num >> 8) & 0x00FF;
    let b = num & 0x0000FF;

    r = Math.round(r + (255 - r) * percent);
    g = Math.round(g + (255 - g) * percent);
    b = Math.round(b + (255 - b) * percent);

    return "#" + (1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1);
};

/**
 * Capitaliza a primeira letra de cada palavra (equivalente ao ucwords do PHP).
 * Pensado para campos de nome em formulários (nome completo, razão social, etc.).
 */
export const ucwords = (value) => {
    if (value == null || value === "") {
        return value ?? "";
    }

    return String(value)
        .toLocaleLowerCase("pt-BR")
        .replace(/(?:^|\s)\S/g, (char) => char.toLocaleUpperCase("pt-BR"));
};

/**
 * Converte o valor para minúsculas. Pode ser combinado com outros helpers de input.
 */
export const lowercase = (value) => {
    if (value == null || value === "") {
        return value ?? "";
    }

    return String(value).toLocaleLowerCase("pt-BR");
};

/**
 * Remove caracteres que não são permitidos em um endereço de e-mail (RFC 5322).
 * Não altera o case — combine com `lowercase` quando quiser forçar minúsculas.
 */
export const emailChars = (value) => {
    if (value == null || value === "") {
        return value ?? "";
    }

    return String(value).replace(/[^a-zA-Z0-9.!#$%&'*+/=?^_`{|}~@-]/g, "");
};

/**
 * Remove caracteres especiais do complemento de endereço.
 * Permite letras, números, espaço, barra (/) e hífen (-).
 */
export const complementChars = (value) => {
    if (value == null || value === "") {
        return value ?? "";
    }

    return String(value).replace(/[^\p{L}\p{N}\s/-]/gu, "");
};

/**
 * Permite letras, acentos, espaços, apóstrofo e hífen (cidade, bairro,
 * nomes de localidade — ex.: Santa Bárbara d'Oeste, Não-Me-Toque).
 */
export const lettersAndAccents = (value) => {
    if (value == null || value === "") {
        return value ?? "";
    }

    return String(value).replace(/[^\p{L}\s'-]/gu, "");
};

export function generateValidCpf() {
    function generateDigit(base, fatorInicial) {
        let total = 0;
        for (let i = 0; i < base.length; i++) {
            total += parseInt(base[i]) * (fatorInicial - i);
        }
        const resto = total % 11;
        return resto < 2 ? 0 : 11 - resto;
    }

    let cpfBase = '';
    for (let i = 0; i < 9; i++) {
        cpfBase += Math.floor(Math.random() * 10);
    }

    if (/^(\d)\1+$/.test(cpfBase)) {
        return generateValidCpf();
    }

    const digito1 = generateDigit(cpfBase, 10);
    const digito2 = generateDigit(cpfBase + digito1, 11);

    return cpfBase + digito1 + digito2;
}
